import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  useTheme,
  Chip,
  Alert,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Science as ScienceIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  Error as ErrorIcon,
  LocationOn as LocationOnIcon,
  Public as PublicIcon,
  NearMe as NearMeIcon
} from '@mui/icons-material';
import { useAuth } from '../../../../../auth/AuthContext';
import ServiceInputForm from './ServiceInputForm';
import ServiceResponseViewer from './ServiceResponseViewer';
import EnvironmentServiceHealthCheck from './EnvironmentServiceHealthCheck';
import MultiEnvironmentServiceDashboard from './MultiEnvironmentServiceDashboard';
import { useServiceTesting } from '../../hooks/useServiceTesting';
import { getServiceConfig } from '../../constants/serviceEndpoints';

const ServiceTester = ({ selectedService, onServiceSelect }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { testService } = useServiceTesting();
  const [serviceInputs, setServiceInputs] = useState({});
  const [testResults, setTestResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const services = [
    { key: 'GEOCODING', label: 'Geocoding', icon: <LocationOnIcon />, color: 'primary' },
    { key: 'ESRI', label: 'ESRI', icon: <PublicIcon />, color: 'secondary' },
    { key: 'NEARBY_SCHOOLS', label: 'Nearby Schools', icon: <NearMeIcon />, color: 'success' }
  ];

  // Reset inputs and results when service changes
  useEffect(() => {
    setServiceInputs({});
    setTestResults(null);
  }, [selectedService]);

  const handleInputChange = (key, value) => {
    setServiceInputs(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTest = async () => {
    if (!selectedService) return;

    setIsLoading(true);
    try {
      const response = await testService(selectedService, serviceInputs);
      setTestResults({
        service: selectedService,
        inputs: serviceInputs,
        response: response,
        timestamp: new Date().toISOString(),
        success: true
      });
    } catch (error) {
      setTestResults({
        service: selectedService,
        inputs: serviceInputs,
        error: error.message,
        timestamp: new Date().toISOString(),
        success: false
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setServiceInputs({});
    setTestResults(null);
  };

  const selectedServiceConfig = selectedService ? getServiceConfig(selectedService) : null;
  const serviceIndex = selectedService ? services.findIndex(s => s.key === selectedService) : -1;
  const currentTabIndex = serviceIndex >= 0 ? serviceIndex : false;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          color: theme.palette.primary.main 
        }}>
          <ScienceIcon fontSize="large" />
          Service Testing Suite
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Test individual development Cloud Run services in isolation - geocoding, ESRI, and nearby schools
        </Typography>
        
        {/* Authentication Status */}
        <Box sx={{ mt: 2 }}>
          <Chip
            icon={user ? <CheckIcon /> : <ErrorIcon />}
            label={user ? `Authenticated as ${user.email}` : 'Not authenticated'}
            color={user ? 'success' : 'error'}
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Service Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => {
            if (newValue >= 0) {
              onServiceSelect(services[newValue].key);
            }
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {services.map((service, index) => (
            <Tab
              key={service.key}
              icon={service.icon}
              label={service.label}
              iconPosition="start"
              sx={{ 
                minHeight: 64,
                textTransform: 'none',
                color: `${service.color}.main`
              }}
            />
          ))}
        </Tabs>
      </Paper>

      {/* Main Content */}
      {!selectedService ? (
        /* Dashboard View - No Service Selected */
        <MultiEnvironmentServiceDashboard onServiceSelect={onServiceSelect} />
      ) : (
        /* Service Testing View */
        <Box>
          {/* Service Health Check */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <EnvironmentServiceHealthCheck selectedService={selectedService} />
          </Paper>

          {/* Service Input and Response */}
          <Box sx={{ display: 'flex', gap: 3, minHeight: '60vh' }}>
            {/* Left Side - Service Input */}
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">
                    Test {selectedServiceConfig?.name || selectedService}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<PlayArrowIcon />}
                      onClick={handleTest}
                      disabled={!user || isLoading}
                      size="large"
                    >
                      {isLoading ? 'Testing...' : 'Run Test'}
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleReset}
                      disabled={isLoading}
                    >
                      Reset
                    </Button>
                  </Box>
                </Box>

                {!user && (
                  <Alert severity="warning" sx={{ mb: 3 }}>
                    You must be logged in to test services.
                  </Alert>
                )}

                <ServiceInputForm
                  selectedService={selectedService}
                  inputs={serviceInputs}
                  onInputChange={handleInputChange}
                  disabled={!user || isLoading}
                />
              </CardContent>
            </Card>

            {/* Right Side - Service Response */}
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Response Data
                </Typography>
                <ServiceResponseViewer 
                  results={testResults}
                  isLoading={isLoading}
                />
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default ServiceTester; 